import * as XLSX from 'xlsx';
import type { POSCustomField } from '@/types';
import type { BulkSelectableRow } from '@/hooks/useBulkItemSelection';

export interface BulkUpdateExportRow {
  item_id: string;
  variant_id: string;
  item_code: string;
  barcode: string;
  item_name: string;
  variant_name: string;
  category: string;
  uom: string;
  sale_price: number | string;
  wholesale_price: number | string;
  reseller_price: number | string;
  minimum_price: number | string;
  cost_price: number | string;
  maintain_stock: string;
  is_weighted: string;
  plu_code: string;
  custom_fields?: Record<string, string>;
}

const BASE_HEADERS = [
  'Item ID (DO NOT EDIT)',
  'Variant ID (DO NOT EDIT)',
  'Item Code (DO NOT EDIT)',
  'Barcode (DO NOT EDIT)',
  'Item Name',
  'Variant Name',
  'Category',
  'UOM',
  'Sale Price',
  'Wholesale Price',
  'Reseller Price',
  'Minimum Price',
  'Cost Price',
  'Maintain Stock',
  'Is Weighted',
  'PLU Code',
] as const;

const INSTRUCTIONS = [
  ['Bulk Product Update — Instructions'],
  [''],
  ['DO NOT change these columns (used to match rows in the system):'],
  ['  • Item ID (DO NOT EDIT)'],
  ['  • Variant ID (DO NOT EDIT)'],
  ['  • Item Code (DO NOT EDIT)'],
  ['  • Barcode (DO NOT EDIT)'],
  [''],
  ['You MAY edit: Item Name, Variant Name, Category, UOM, prices, stock flags, PLU, and custom field columns.'],
  ['Leave a cell blank to keep the current value unchanged.'],
  ['For products with variants, each variant is a separate row — edit Variant Name and prices on that row.'],
  ['Category must match an existing category name exactly.'],
  ['Maintain Stock / Is Weighted: use Yes or No.'],
  ['After editing, save this file and upload it via Bulk Update → Import updated file.'],
];

function flagLabel(v: unknown): string {
  if (v === 1 || v === true || v === '1') return 'Yes';
  return 'No';
}

export function buildExportRowsFromSelection(
  rows: BulkSelectableRow[],
  customFields: POSCustomField[] = [],
): { headers: string[]; dataRows: string[][] } {
  const cfHeaders = customFields.map((cf) => cf.label || cf.fieldname);
  const headers = [...BASE_HEADERS, ...cfHeaders];

  const dataRows = rows.map((row) => {
    const item = row.item;
    const variant = row.variant;
    const cfData = (item.custom_fields_data || {}) as Record<string, string>;
    const base: string[] = [
      item.name,
      variant?.name || '',
      item.item_code || '',
      variant?.barcode || item.barcode || '',
      item.item_name || '',
      variant?.variant_name || '',
      row.categoryName || '',
      variant?.unit_of_measurement || item.unit_of_measurement || '',
      String(variant ? (variant.price ?? 0) : (item.sale_price ?? 0)),
      String(variant ? (variant.wholesale_price ?? 0) : (item.wholesale_price ?? 0)),
      String(variant ? (variant.reseller_price ?? 0) : (item.reseller_price ?? 0)),
      String(variant ? (variant.minimum_price ?? 0) : (item.minimum_price ?? 0)),
      String(variant ? (variant.cost_price ?? 0) : (item.cost_price ?? 0)),
      flagLabel(item.maintain_stock),
      flagLabel(item.is_weighted),
      item.plu_code || '',
    ];
    for (const cf of customFields) {
      base.push(String(cfData[cf.fieldname] ?? ''));
    }
    return base;
  });

  return { headers, dataRows };
}

export function downloadBulkProductUpdateExcel(
  rows: BulkSelectableRow[],
  customFields: POSCustomField[] = [],
  filename = 'POS_Bulk_Update.xlsx',
): void {
  const { headers, dataRows } = buildExportRowsFromSelection(rows, customFields);

  const instructionsWs = XLSX.utils.aoa_to_sheet(INSTRUCTIONS);
  instructionsWs['!cols'] = [{ wch: 72 }];

  const dataWs = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
  dataWs['!cols'] = headers.map((h) => ({ wch: Math.min(28, Math.max(12, h.length + 2)) }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, instructionsWs, 'Instructions');
  XLSX.utils.book_append_sheet(wb, dataWs, 'Products');
  XLSX.writeFile(wb, filename);
}
